import React from 'react';
import { Modal } from '../ui';
import { BomCompleteness } from '../../types/bom.types';
import { ManualEditRequest } from '../../types/bomVersions';
import ExtractedBomSheet from './extracted/ExtractedBomSheet';
import { BomSaveFailure, ExtractedBomRow } from './extracted/types';
import { EnhanceSelection } from '../../utils/bomProposal';

/**
 * The Extracted BOM modal ("Open BOM →" on step 2) — filename + line count in
 * the header, an editable sheet of every line (name, part number, MPN,
 * manufacturer, description, qty, target price), and Robbie's bar as the
 * footer. Presentational: the caller owns saving (see
 * `extracted/ConnectedExtractedBomModal`), so the design gallery renders it
 * with sample rows. Without `onSave` it is the read-only view.
 */
export type { ExtractedBomRow } from './extracted/types';

export interface ExtractedBomModalProps {
  open: boolean;
  onClose: () => void;
  components: ExtractedBomRow[];
  /** The server's count; recomputed locally when absent. */
  completeness?: BomCompleteness | null;
  /** Source file the BOM was extracted from, when known. */
  fileName?: string;
  readOnly?: boolean;
  saving?: boolean;
  onSave?: (request: ManualEditRequest, changedCount: number) => Promise<BomSaveFailure | null>;
  /** "Enhance with Robbie" — the caller closes the sheet and asks him. */
  onEnhanceWithRobbie?: (selection?: EnhanceSelection) => void;
}

export const ExtractedBomModal: React.FC<ExtractedBomModalProps> = ({
  open,
  onClose,
  components,
  completeness,
  fileName,
  readOnly = false,
  saving = false,
  onSave,
  onEnhanceWithRobbie,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    size="sheet"
    flush
    title={
      <span className="flex flex-col">
        <span className="text-[15px] font-bold text-ink">Extracted BOM</span>
        <span className="font-mono text-body-sm font-normal text-ink-muted">
          {fileName ? `${fileName} · ` : ''}
          {components.length} components
        </span>
      </span>
    }
  >
    <ExtractedBomSheet
      components={components}
      completeness={completeness}
      readOnly={readOnly || !onSave}
      saving={saving}
      onSave={onSave ?? (async () => null)}
      onClose={onClose}
      onEnhanceWithRobbie={onEnhanceWithRobbie}
    />
  </Modal>
);

export default ExtractedBomModal;
